import { useEffect } from 'react';
import { Input } from '@/features/ui/primitives/input';

interface SignatureFieldProps {
  id: string;
  value: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  isInvalid?: boolean;
}

// Purpose: Collect a typed full name to serve as the legal electronic signature
export const SignatureField = ({
  id,
  value,
  onChange,
  disabled = false,
  isInvalid = false,
}: SignatureFieldProps) => {
  const typedValue = (value ?? '').toString();

  // Expose a small test helper to programmatically set the signature in tests
  // eslint-disable-next-line react-you-might-not-need-an-effect/you-might-not-need-an-effect
  useEffect(() => {
    if (process.env.NODE_ENV === 'test') {
      const functionName = `setSignature_${id}`;
      (window as any)[functionName] = (text: string) => {
        const next = typeof text === 'string' ? text : '';
        onChange(next);
      };
      return () => {
        delete (window as any)[functionName];
      };
    }
  }, [id, onChange]);

  return (
    <div className={`w-full ${disabled ? 'opacity-70' : ''}`} data-testid={`signature-field-${id}`}>
      <div
        className={`relative rounded-lg p-3 ${
          isInvalid ? 'bg-red-50' : disabled ? 'bg-gray-50' : ''
        }`}
      >
        <Input
          id={`signature-input-${id}`}
          value={typedValue}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder="Type your full legal name"
          className={`text-sm h-8 ${isInvalid ? 'border-red-500 bg-red-50' : ''} ${
            disabled ? 'bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed' : ''
          }`}
          aria-describedby={`signature-helper-${id}`}
          aria-invalid={isInvalid}
        />

        {!disabled && (
          <p id={`signature-helper-${id}`} className="mt-2 text-[11px] leading-4 text-gray-500">
            By typing your full name, you agree this is your electronic signature.
          </p>
        )}

        {/* Generated Signature Preview */}
        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-700 mb-1">Generated Signature</label>
          <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            aria-label="Generated signature preview"
            className="border border-gray-300 rounded-md bg-white p-4 h-28 flex items-center"
          >
            <span
              className="w-full text-center text-3xl text-gray-800"
              style={{ fontFamily: '"Alex Brush","Zapfino","Snell Roundhand","Segoe Script","Bradley Hand","Brush Script MT","Lucida Handwriting",cursive' }}
            >
              {typedValue}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
